import { createRoot } from "react-dom/client";
import type { BillingDocument } from "@/core/billing/billing-documents/types";
import { BILLING_DOC_STATUS_LABELS } from "@/core/billing/billing-documents/types";
import { formatMoney } from "@/core/pharmacy/format";

interface BillingDocumentPrintSheetProps {
  document: BillingDocument;
}

export function BillingDocumentPrintSheet({ document: doc }: BillingDocumentPrintSheetProps) {
  const isProforma = doc.status === "proforma" || doc.kind === "proforma";
  const title = isProforma ? "FACTURE PROFORMA" : "FACTURE";
  const issuedLabel = doc.issuedAt
    ? new Date(doc.issuedAt).toLocaleString("fr-FR")
    : new Date(doc.createdAt).toLocaleString("fr-FR");

  return (
    <div className="ph-invoice-print-sheet">
      <div className="ph-invoice-header">
        <div>
          <div className="ph-invoice-brand">ITaaS Hospitol</div>
          <div className="ph-invoice-sub">Comptabilité — {BILLING_DOC_STATUS_LABELS[doc.status]}</div>
        </div>
        <div className="ph-invoice-meta text-end">
          <div className="ph-invoice-title">{title}</div>
          <div className="fw-semibold">{doc.number}</div>
          <div className="small text-muted">{issuedLabel}</div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Client / patient</div>
          <div className="fw-semibold">{doc.customer.name}</div>
          {doc.customer.phone && (
            <div className="small text-muted">Tél. {doc.customer.phone}</div>
          )}
        </div>
        <div className="col-md-6 text-md-end">
          <div className="ph-invoice-section-title">Payeur</div>
          <div className="fw-semibold">{doc.payerName}</div>
          <div className="small text-muted">{doc.payerType}</div>
        </div>
      </div>

      {isProforma && doc.proformaValidUntil && (
        <p className="small text-warning mb-3">
          Proforma valable jusqu&apos;au{" "}
          {new Date(doc.proformaValidUntil).toLocaleDateString("fr-FR")}
        </p>
      )}

      <table className="table table-sm ph-invoice-table">
        <thead>
          <tr>
            <th>Article</th>
            <th className="text-end">Prix unit.</th>
            <th className="text-end">Qté</th>
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.map((line) => (
            <tr key={line.id}>
              <td>{line.label}</td>
              <td className="text-end">{formatMoney(line.unitPrice)}</td>
              <td className="text-end">{line.quantity}</td>
              <td className="text-end fw-semibold">
                {formatMoney(line.unitPrice * line.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ph-invoice-footer">
        <div className="small text-muted">
          {isProforma
            ? "Document proforma — sans valeur comptable définitive."
            : "Facture définitive — à présenter en caisse pour paiement."}
        </div>
        <div className="ph-invoice-totals">
          <div className="d-flex justify-content-between ph-invoice-grand">
            <span>Total</span>
            <strong>{formatMoney(doc.subtotal)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

export function printBillingDocument(doc: BillingDocument): void {
  const portal = document.createElement("div");
  portal.className = "ph-print-portal";
  portal.setAttribute("aria-hidden", "true");
  document.body.appendChild(portal);

  const root = createRoot(portal);
  root.render(<BillingDocumentPrintSheet document={doc} />);

  window.setTimeout(() => {
    window.print();
    root.unmount();
    portal.remove();
  }, 150);
}
